import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { TListsListing } from "../types"
import {
    Button,
    DataTable,
    DialogHandler,
    Dropdown,
    EmptyContent,
    fetchRoute,
    formatNumber,
    FullBar,
    GenericWrapperContext,
    getCalendarFormat,
    Header,
    Icon,
    ListModel,
    ListsFactory,
    LoadingContainer,
    Search,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TListModel,
    TOrder,
    TPromisePoolPartialListReport,
    TPromisePoolRef,
    TPromisePoolReturnData
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import { Stack } from "@mui/material"
import { ERoutes } from "../../../utils/url"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { listReportsPromisePool } from "../../../utils/promisesPools"
import { affixToPercent } from "../../../utils/data"

export default function ListsListing({ }: TListsListing) {
    const { t } = useTranslation()
    const genericWrapper = useContext(GenericWrapperContext)

    const [loading, setLoading] = useState(false)
    const [lists, setLists] = useState<TGenericListReturn<ListModel>>()
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<keyof TListModel>("created_on")
    const [search, setSearch] = useState("")
    const [openedSearch, setOpenedSearch] = useState(false)

    const [partialListReports, setPartialListReports] = useState<TPromisePoolReturnData<TPromisePoolPartialListReport>>({})
    const partialListReportsPromiseRef = useRef<TPromisePoolRef>(undefined)

    useEffect(() => {
        fetchLists()
    }, [page, perPage, order, orderBy, search])

    useEffect(handleSearchFocus, [openedSearch])

    useEffect(() => {
        return () => {
            stopPromisePools()
        }
    }, [])

    function stopPromisePools() {
        try {
            partialListReportsPromiseRef.current?.stop()
            partialListReportsPromiseRef.current = undefined
        } catch (e) {/* pass */ }
    }

    function fetchLists() {
        setLoading(true)
        ListsFactory.list({
            withCount: true,
            page: page,
            perPage: perPage,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            filter: search ? `name==*${search}*` : undefined,
        }).then((data) => {
            listReportsPromisePool({ data: data.data, setter: setPartialListReports, poolRef: partialListReportsPromiseRef })
            setLists(data)
        }).catch(() => {
            showErrorDialog()
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as keyof TListModel)
    }

    function showErrorDialog(e?: any) {
        if (e) {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
        }
    }


    function handlePaginationChange(cPage: number) {
        setPage(cPage)
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".listsSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }
    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleCreateList() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.createList })
    }

    function handleViewContacts(list: ListModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.contacts, params: { listId: list.id } }) + "?listId=" + list.id
    }

    function handleEditList(list: ListModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.listEdit, params: { listId: list.id } })
    }

    function handleRenameList(list: ListModel) {
        showRenameDialog({ initName: list.name, resourceKey: "general-list_one" })
            .then(({ value }) => {
                return list.save({ name: value })
            }).then(() => {
                fetchLists()
                DialogHandler.hide()
            }).catch((e) => {
                showErrorDialog(e)
            })
    }

    function handleDeleteList(list: ListModel) {
        showDeleteDialog({
            name: list.name,
            resourceKey: "general-list_one",
        }).then(({ btnRef }) => {
            btnRef.animate()
            return list.delete()
        }).then(() => {
            DialogHandler.show({
                type: "success",
                isExpress: true,
                title: t("general-resource-deleted", { resource: t("general-list_one") })
            })
            fetchLists()
        }).catch((e) => {
            showErrorDialog(e)
        })
    }

    const colsData: TDataTableColumnsData[] = useMemo(() => {
        return [
            {
                id: "name",
                label: t("general-list_one"),
                sortable: true
            },
            {
                id: "subscribers",
                label: t("general-subscriber_other")
            },
            {
                id: "click_rate",
                label: t("general-click_rate"),
            },
            {
                id: "created_on",
                label: t("general-created_on"),
                sortable: true
            },
            {
                id: "actions",
                label: ""
            }
        ]
    }, [])


    const rows: any[] = lists && lists.data.map((list: ListModel, index) => {
        const partialListReport: Partial<TPromisePoolPartialListReport> = partialListReports?.[list.id] || {}
        const actions: any = <Stack direction="row" gap={3}>
            <Button
                variant="outlined"
                size="small"
                color="body1"
                onClick={() => { handleViewContacts(list) }}
                iconOnly
                disabled={loading}
            >{t("general-view-contact_other")}</Button>
            <Dropdown
                size="medium"
                color="body1"
                variant="outlined"
                iconOnly
                options={[
                    {
                        isAction: true,
                        text: t("general-edit"),
                        postContent: <Icon name="Edit18" />,
                        onClick: () => { handleEditList(list) }
                    },
                    {
                        isAction: true,
                        text: t("general-rename"),
                        postContent: <Icon name="Field18" />,
                        onClick: () => { handleRenameList(list) }
                    },
                    {
                        isAction: true,
                        text: t("general-delete"),
                        postContent: <Icon name="Delete18" />,
                        onClick: () => { handleDeleteList(list) }
                    }
                ]}
            />
        </Stack>
        return {
            name: list.name,
            subscribers: formatNumber(partialListReport?.active_contacts || 0, genericWrapper.user?.language),
            click_rate: affixToPercent(partialListReport?.click_rate || 0, genericWrapper.user?.language),
            created_on: getCalendarFormat({ unixDate: list.created_on }),
            actions: actions
        }
    }) || []

    return <Stack className="listsListingContainer" gap={8}>

        <FullBar
            className={loading ? "loading" : ""}
            left={<Header
                heading={t("general-list_other")}
                headingVariant="titleL"
                support={genericWrapper.account?.name}
                headingTooltip={t("lists-heading-tooltip")}
            />}
            right={[
                <Button
                    key="create-list"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    onClick={handleCreateList}
                    iconOnly
                    disabled={loading}
                ><Icon name="Add18" /></Button>,
                <Button
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleSearchOpen}
                    iconOnly
                    disabled={loading}
                ><Icon name="Search18" /></Button>
            ]}
        />

        {openedSearch &&
            <Search placeholder={t("list-search-placeholder")} initValue={search} className={`listsSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
        }

        <LoadingContainer loading={!lists}>
            {lists && lists.data.length === 0 && !search &&
                <EmptyContent
                    title={t("lists-listing-empty-title")}
                    desc={t("lists-listing-empty-desc")}
                    iconName="CreateForm44"
                    button={<Button
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateList} iconOnly>
                        {t("general-create-list")}
                    </Button>}
                />
            }

            {lists && lists.data.length === 0 && search &&
                <EmptyContent
                    title={t("list-listing-empty-search-title")}
                    desc={t("search-empty-desc")}
                />
            }

            {lists && lists.data.length > 0 &&
                <DataTable
                    initState={{
                        rowsPerPage: perPage,
                        order,
                        orderBy,
                    }}
                    totalCount={lists?.pagination?.count}
                    className={`listsDataTable ${loading ? "loading" : ""}`}
                    onPaginationChange={handlePaginationChange}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    stickyLastCol
                />
            }
        </LoadingContainer>
    </Stack>
}